(function () {
  'use strict';

  function buildSubscription(fields, listId, source) {
    const email = fields.email.trim();
    const name = fields.name.trim();
    const phone = fields.phone.trim().replace(/[\s().-]/g, '');
    if (phone && !/^\+[1-9]\d{6,14}$/.test(phone)) {
      const error = new Error('Enter a phone number with its country code, such as +1 202 555 0146, or leave it blank.');
      error.field = 'phone';
      throw error;
    }
    const attributes = {
      email,
      // Explicitly subscribe email only, even when a phone number is provided.
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
      properties: { 'Junglee Signup Source': source }
    };
    if (name) {
      const parts = name.split(/\s+/);
      attributes.first_name = parts.shift();
      if (parts.length) attributes.last_name = parts.join(' ');
      attributes.properties['Junglee Waitlist Name'] = name;
    }
    if (phone) attributes.phone_number = phone;
    return { data: {
      type: 'subscription',
      attributes: { custom_source: source, profile: { data: { type: 'profile', attributes } } },
      relationships: { list: { data: { type: 'list', id: listId } } }
    } };
  }

  async function sendSubscription(fetchFn, siteId, payload, signal) {
    const response = await fetchFn('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(siteId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2026-07-15' },
      body: JSON.stringify(payload), signal, credentials: 'omit'
    });
    if (response.status === 202) return;
    const error = new Error(response.status === 429
      ? 'Too many attempts right now. Please wait a minute and try again.'
      : 'We couldn’t confirm your signup. Check your email and phone number, then try again.');
    error.status = response.status;
    throw error;
  }

  // Local contract tests supply a fake transport; they never create subscribers.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildSubscription, sendSubscription };
    return;
  }

  class JungleeKlaviyo extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const form = this.querySelector('form');
      if (!form) return;
      this.ready = true;
      const fieldset = form.querySelector('fieldset');
      const message = this.querySelector('[data-jw-message]');
      const button = form.querySelector('button');
      const siteId = (this.dataset.siteId || '').trim();
      const listId = (this.dataset.listId || '').trim();
      const configured = /^[a-zA-Z0-9]{6}$/.test(siteId) && /^[a-zA-Z0-9]{6}$/.test(listId);
      const phone = form.elements['contact[phone]'];
      phone.addEventListener('input', () => phone.setCustomValidity(''));
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (!configured || this.pending || !form.reportValidity()) return;
        let payload;
        try {
          payload = buildSubscription({
            name: form.elements['contact[name]'].value,
            email: form.elements['contact[email]'].value,
            phone: phone.value
          }, listId, this.dataset.source || 'junglee-12oz-waitlist');
        } catch (error) {
          phone.setCustomValidity(error.message);
          phone.reportValidity();
          return;
        }
        this.pending = true;
        fieldset.disabled = true;
        form.setAttribute('aria-busy', 'true');
        message.setAttribute('role', 'status');
        message.hidden = false;
        message.textContent = 'Joining the waitlist…';
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        try {
          await sendSubscription(window.fetch.bind(window), siteId, payload, controller.signal);
          message.textContent = 'Welcome to the Junglee Tribe. Keep an eye on your inbox — that’s where the launch news lands first';
          button.querySelector('span').textContent = 'Signup received';
        } catch (error) {
          message.setAttribute('role', 'alert');
          message.textContent = error.status ? error.message : 'We couldn’t confirm your signup. Please check your connection and try again.';
          fieldset.disabled = false;
        } finally {
          clearTimeout(timeout);
          this.pending = false;
          form.removeAttribute('aria-busy');
        }
      });
      if (configured) {
        fieldset.disabled = false;
        message.hidden = true;
      }
    }
  }
  if (!customElements.get('junglee-klaviyo')) customElements.define('junglee-klaviyo', JungleeKlaviyo);
}());
